import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Zap, Menu, X, ChevronDown, Cpu, BookOpen,
  FolderOpen, GraduationCap, LogIn, LogOut, User,
  Crown, Terminal, Shield, Plane, FlaskConical, Layers
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import GoogleSignInButton from './GoogleSignInButton';

const navItems = [
  { path: '/diagnose', label: 'Diagnosis', icon: Cpu, color: 'green' },
  { path: '/components', label: 'Parts', icon: Layers, color: 'cyan' },
  { path: '/schematics', label: 'Diagrams', icon: BookOpen, color: 'amber' },
  { path: '/projects', label: 'Projects', icon: FolderOpen, color: 'purple' },
  { path: '/recipes', label: 'Recipes', icon: FlaskConical, color: 'amber' },
  { path: '/learn', label: 'Learn', icon: GraduationCap, color: 'cyan' },
  { path: '/studio', label: 'Studio', icon: Terminal, color: 'amber' },
  { path: '/drones', label: 'Drones', icon: Plane, color: 'green' },
];

const colorMap: Record<string, string> = {
  green: 'var(--color-primary)',
  cyan: 'var(--color-secondary)',
  amber: 'var(--color-accent)',
  purple: 'var(--color-purple)',
};

export default function GlobalHeader() {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [loginModalOpen, setLoginModalOpen] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  // Close menus on route change
  useEffect(() => {
    setMobileOpen(false);
    setUserMenuOpen(false);
  }, [location.pathname]);

  const isActive = (path: string) => location.pathname === path;

  return (
    <>
      <header
        className="header"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 100,
          background: scrolled
            ? 'rgba(5, 9, 18, 0.95)'
            : 'rgba(5, 9, 18, 0.85)',
          backdropFilter: 'blur(20px)',
          borderBottom: scrolled
            ? '1px solid rgba(0, 255, 136, 0.15)'
            : '1px solid rgba(30, 45, 66, 0.5)',
          transition: 'all 0.3s ease',
        }}
      >
        <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '0 1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', height: '64px', gap: '2rem' }}>

            {/* Logo */}
            <Link
              to="/"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.4rem',
                textDecoration: 'none',
                flexShrink: 0,
              }}
            >
              <div style={{
                width: '30px',
                height: '30px',
                background: 'linear-gradient(135deg, #00ff88, #00cfff)',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 0 10px rgba(0,255,136,0.3)',
              }}>
                <Zap size={16} color="#050912" strokeWidth={2.5} />
              </div>
              <div className="header-logo-text">
                <span style={{
                  fontWeight: 800,
                  fontSize: '0.9rem',
                  letterSpacing: '-0.02em',
                  background: 'linear-gradient(135deg, #00ff88, #00cfff)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}>
                  DIYElectronicMech
                </span>
                <div className="header-logo-sub" style={{
                  fontSize: '0.55rem',
                  color: 'var(--text-muted)',
                  letterSpacing: '0.08em',
                  fontFamily: 'var(--font-mono)',
                  marginTop: '-2px',
                }}>
                  FIX · BUILD · UNDERSTAND
                </div>
              </div>
            </Link>


            {/* Desktop Nav */}
            <nav style={{ display: 'flex', alignItems: 'center', gap: '0.2rem', flex: 1 }}
              className="desktop-nav">
              {navItems.map(({ path, label, icon: Icon, color }) => (
                <Link
                  key={path}
                  to={path}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.4rem',
                    padding: '0.45rem 0.75rem',
                    borderRadius: '8px',
                    fontSize: '0.85rem',
                    fontWeight: 500,
                    textDecoration: 'none',
                    transition: 'all 0.2s ease',
                    color: isActive(path) ? colorMap[color] : 'var(--text-secondary)',
                    background: isActive(path) ? `rgba(0,255,136,0.08)` : 'transparent',
                    border: isActive(path) ? `1px solid ${colorMap[color]}33` : '1px solid transparent',
                  }}
                  onMouseEnter={e => {
                    if (!isActive(path)) {
                      (e.currentTarget as HTMLElement).style.color = colorMap[color];
                      (e.currentTarget as HTMLElement).style.background = `${colorMap[color]}0d`;
                    }
                  }}
                  onMouseLeave={e => {
                    if (!isActive(path)) {
                      (e.currentTarget as HTMLElement).style.color = 'var(--text-secondary)';
                      (e.currentTarget as HTMLElement).style.background = 'transparent';
                    }
                  }}
                >
                  <Icon size={15} />
                  {label}
                </Link>
              ))}
            </nav>

            {/* Right Side */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexShrink: 0 }}>

              {/* Online indicator */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}
                className="desktop-only"
                title="ElectrAI Reasoning Engine"
              >
              </div>

              {user ? (
                <div style={{ position: 'relative' }}>
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.4rem',
                      background: 'var(--bg-surface)',
                      border:scrolled ? '1px solid var(--border-primary)' : '1px solid var(--border-color)',
                      borderRadius: '8px',
                      padding: '0.4rem 0.75rem',
                      color: 'var(--text-primary)',
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                      transition: 'all 0.2s ease',
                      boxShadow: scrolled ? '0 0 15px rgba(0,255,136,0.1)' : 'none',
                    }}
                  >
                    <User size={14} className={scrolled ? 'glow-text-green' : ''} />
                    <span style={{ maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600 }}>
                      {user.name || user.email.split('@')[0]}
                    </span>
                    <ChevronDown size={12} />
                  </button>

                  {userMenuOpen && (
                    <div style={{
                      position: 'absolute',
                      top: 'calc(100% + 12px)',
                      right: 0,
                      background: 'var(--bg-surface)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '14px',
                      padding: '0.75rem',
                      minWidth: '220px',
                      boxShadow: '0 12px 40px rgba(0,0,0,0.6)',
                      zIndex: 200,
                      animation: 'slide-up 0.2s ease-out forwards',
                    }}>
                      <div style={{ padding: '0 0.5rem 0.5rem' }}>
                        <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.25rem' }}>User Context</div>
                        <div className="drtkng-core" style={{ width: '100%', justifyContent: 'center' }}>
                          <span className="status-dot online" style={{ width: '6px', height: '6px', marginRight: '4px' }} />
                          ELECTRAI CORE // {user.name?.split(' ')[0] || 'MEMBER'}
                        </div>
                      </div>
                      
                      <div style={{ height: '1px', background: 'var(--border-color)', margin: '0.5rem 0' }} />
                      
                      <Link to="/upgrade" style={{
                        display: 'flex', alignItems: 'center', gap: '0.6rem',
                        padding: '0.6rem 0.75rem', borderRadius: '8px',
                        color: 'var(--color-accent)', textDecoration: 'none',
                        fontSize: '0.85rem', fontWeight: 600,
                        transition: 'background 0.2s',
                      }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,170,0,0.08)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <Crown size={15} /> Pricing & Pro
                      </Link>
                      
                      <Link to="/diagnose" style={{
                        display: 'flex', alignItems: 'center', gap: '0.6rem',
                        padding: '0.6rem 0.75rem', borderRadius: '8px',
                        color: 'var(--text-secondary)', textDecoration: 'none',
                        fontSize: '0.85rem',
                        transition: 'background 0.2s',
                      }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-surface-2)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <Shield size={15} /> My Saved Diagnostics
                      </Link>

                      <div style={{ height: '1px', background: 'var(--border-color)', margin: '0.5rem 0' }} />
                      
                      <button
                        onClick={() => { logout(); setUserMenuOpen(false); }}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '0.6rem',
                          padding: '0.6rem 0.75rem', borderRadius: '8px',
                          color: 'var(--color-danger)', background: 'none',
                          border: 'none', cursor: 'pointer', fontSize: '0.85rem',
                          width: '100%', textAlign: 'left', fontWeight: 500,
                          transition: 'background 0.2s',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,68,102,0.08)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <LogOut size={15} /> Sign Out
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <button
                    onClick={() => setLoginModalOpen(true)}
                    className="btn btn-ghost btn-sm desktop-only"
                    style={{ border: '1px solid var(--border-color)', color: 'var(--text-secondary)' }}
                  >
                    <LogIn size={14} /> Sign In
                  </button>
                  <Link to="/upgrade" className="btn btn-primary btn-sm" style={{ fontWeight: 700 }}>
                    <Zap size={13} strokeWidth={3} /> Pricing & Pro
                  </Link>
                </>
              )}

              {/* Mobile hamburger */}
              <button
                className="mobile-only"
                onClick={() => setMobileOpen(!mobileOpen)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--text-primary)',
                  cursor: 'pointer',
                  padding: '0.4rem',
                }}
              >
                {mobileOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile nav drawer */}
        {mobileOpen && (
          <div style={{
            position: 'fixed',
            top: '64px',
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(5, 9, 18, 0.98)',
            backdropFilter: 'blur(20px)',
            borderTop: '1px solid var(--border-color)',
            padding: '1.5rem',
            zIndex: 1000,
            overflowY: 'auto',
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {navItems.map(({ path, label, icon: Icon, color }) => (
                <Link
                  key={path}
                  to={path}
                  onClick={() => setMobileOpen(false)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    padding: '1rem 0.75rem',
                    borderRadius: '10px',
                    color: isActive(path) ? colorMap[color] : 'var(--text-secondary)',
                    textDecoration: 'none',
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    background: isActive(path) ? 'rgba(0, 255, 136, 0.05)' : 'transparent',
                    border: isActive(path) ? '1px solid rgba(0, 255, 136, 0.1)' : 'none',
                  }}
                >
                  <div style={{ width: '32px', height: '32px', background: `${colorMap[color]}15`, borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon size={20} color={colorMap[color]} />
                  </div>
                  {label}
                </Link>
              ))}
            </div>
            <div style={{ marginTop: '2rem', display: 'flex', gap: '1rem', flexDirection: 'column' }}>
              {user ? (
                <button
                  onClick={() => { logout(); setMobileOpen(false); }}
                  className="btn btn-ghost"
                  style={{ width: '100%', height: '54px' }}
                >
                  <LogOut size={18} /> Sign Out
                </button>
              ) : (
                <button
                  onClick={() => { setLoginModalOpen(true); setMobileOpen(false); }}
                  className="btn btn-ghost"
                  style={{ width: '100%', height: '54px' }}
                >
                  <LogIn size={18} /> Sign In
                </button>
              )}
              <Link to="/upgrade" onClick={() => setMobileOpen(false)} className="btn btn-primary" style={{ width: '100%', height: '54px', justifyContent: 'center' }}>
                <Zap size={20} /> Get Pro Access
              </Link>
            </div>
          </div>
        )}

        {/* Login Modal Overlay */}
        {loginModalOpen && (
          <div onClick={() => setLoginModalOpen(false)} style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(5,11,20,0.92)', backdropFilter: 'blur(12px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '1.5rem',
          }}>
            <div onClick={e => e.stopPropagation()} style={{
              background: 'var(--bg-surface)',
              border: '1px solid var(--border-color)',
              borderRadius: '24px',
              padding: '2.5rem 2rem',
              maxWidth: '400px',
              width: '100%',
              textAlign: 'center',
              boxShadow: '0 0 60px rgba(0,255,136,0.12)',
              animation: 'slide-up 0.3s ease-out forwards',
              position: 'relative'
            }}>
              <button 
                onClick={() => setLoginModalOpen(false)}
                style={{ position: 'absolute', top: '1rem', right: '1rem', background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}
              >
                <X size={20} />
              </button>

              <div style={{
                width: '64px', height: '64px',
                background: 'rgba(0,255,136,0.08)',
                border: '1px solid rgba(0,255,136,0.25)',
                borderRadius: '16px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 1.5rem',
                boxShadow: '0 0 20px rgba(0,255,136,0.1)',
              }}>
                <LogIn size={28} color="var(--color-primary)" />
              </div>

              <h2 style={{ fontSize: '1.5rem', fontWeight: 800, marginBottom: '0.75rem', letterSpacing: '-0.02em' }}>
                Sign In to <span className="glow-text-green">Vault</span>
              </h2>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '2rem', lineHeight: 1.6 }}>
                Securely save your diagnostic sessions, part lists, and circuit diagrams across all your devices.
              </p>
              
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <GoogleSignInButton width={320} onSuccess={() => setLoginModalOpen(false)} />
              </div>
              
              <div style={{ marginTop: '2rem', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                By signing in, you agree to our Terms of Service.
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Spacer to push content below fixed header */}
      <div style={{ height: '64px' }} />

      <style>{`
        @media (max-width: 768px) {
          .desktop-nav { display: none !important; }
          .desktop-only { display: none !important; }
        }
        @media (min-width: 769px) {
          .mobile-only { display: none !important; }
        }
      `}</style>
    </>
  );
}
